import { ChevronDown, Plus, X, Calendar, Trash2 } from 'lucide-react';
import { createPortal } from 'react-dom';
import { Formik, Form, Field, ErrorMessage, FieldArray } from 'formik';
import * as Yup from 'yup';
import { createCommittee } from '../../services/committeeService';

interface Member {
  source: 'system' | 'manual';
  name: string;
  email: string;
  title: string;
  role: 'Internal' | 'External';
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
}

// Validation Schema
const AddCommitteeSchema = Yup.object().shape({
  committeeStatus: Yup.string().required('Committee status is required'),
  name: Yup.string().required('Committee name is required').min(3, 'Name must be at least 3 characters'),
  type: Yup.string().required('Committee type is required'),
  authorityLevel: Yup.string().required('Authority level is required'),
  purpose: Yup.string().max(500, 'Purpose cannot exceed 500 characters'),
  chairmanName: Yup.string().required('Chairman name is required'),
  chairmanEmail: Yup.string().email('Invalid email address').required('Chairman email is required'),
  secretary: Yup.string().required('Secretary is required'),
  meetingFrequency: Yup.string().required('Meeting frequency is required'),
  nextMeetingDate: Yup.date().nullable().required('Next meeting date is required'),
  quorumRequired: Yup.boolean().required(),
  minimumAttendees: Yup.number().min(1, 'Minimum attendees must be at least 1').required('Minimum attendees is required'),
  members: Yup.array().of(
    Yup.object().shape({
      name: Yup.string().required('Member name is required'),
      email: Yup.string().email('Invalid email address'),
      title: Yup.string(),
      role: Yup.string(),
    }),
  ),
});

const inputClass =
  'w-full rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-700 placeholder:text-slate-400 focus:border-emerald-400 focus:outline-none focus:ring-1 focus:ring-emerald-400';

const errorClass = 'mt-1 text-xs text-rose-500';

const AddCommitteeDrawer = ({ isOpen, onClose }: Props) => {
  const initialValues = {
    committeeStatus: 'new',
    name: '',
    type: 'Board',
    authorityLevel: 'board level',
    purpose: '',
    chairmanName: '',
    chairmanEmail: '',
    secretary: 'Abdullah Al-Jabri',
    meetingFrequency: 'as needed',
    nextMeetingDate: '2026-06-29',
    quorumRequired: true,
    minimumAttendees: 2,
    members: [
      { source: 'system', name: 'seyos38194', email: '', title: '', role: 'Internal' },
      { source: 'manual', name: 'seyos38194', email: '', title: '', role: 'Internal' },
    ] as Member[],
    notes: '',
  };

  return createPortal(
    <>
      {/* Drawer Overlay */}
      {isOpen && <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm transition-opacity" onClick={() => onClose()} />}

      {/* Right Side Drawer */}
      <div
        className={`fixed right-0 top-0 z-50 flex h-full w-full max-w-5xl flex-col bg-white shadow-2xl transition-transform duration-300 ease-in-out ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        {/* Header */}
        <div className=" px-6 py-5">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-slate-800">Add Committee</h2>
            <button onClick={onClose} className="rounded-full p-1 text-slate-500 hover:bg-slate-100" aria-label="Close">
              <X size={20} />
            </button>
          </div>
        </div>

        <Formik
          initialValues={initialValues}
          validationSchema={AddCommitteeSchema}
          onSubmit={async (values, { setSubmitting, resetForm }) => {
            try {
              const payload = {
                committeeStatus: values.committeeStatus,
                name: values.name,
                type: values.type,
                authorityLevel: values.authorityLevel,
                purpose: values.purpose,
                chairmanName: values.chairmanName,
                chairmanEmail: values.chairmanEmail,
                secretary: values.secretary,
                meetingFrequency: values.meetingFrequency,
                nextMeetingDate: values.nextMeetingDate,
                quorumRequired: values.quorumRequired,
                minimumAttendees: values.minimumAttendees,
                members: values.members,
                notes: values.notes,
              };

              await createCommittee(payload);

              alert('Committee created successfully!');
              resetForm();
              onClose();
            } catch (error) {
              console.error('Create Committee Error:', error);
              alert('Failed to create committee');
            } finally {
              setSubmitting(false);
            }
          }}
        >
          {({ values, isSubmitting, setFieldValue }) => (
            <>
              {/* Form Content */}
              <div className="flex-1 overflow-y-auto px-6 py-5">
                <Form className="space-y-5">
                  {/* Committee Status */}
                  <div>
                    <label className="mb-2 block text-sm font-medium text-slate-700">Committee Status</label>
                    <div className="flex gap-3">
                      <button
                        type="button"
                        onClick={() => setFieldValue('committeeStatus', 'new')}
                        className={`rounded-lg border px-4 py-2 text-sm font-medium transition-colors ${
                          values.committeeStatus === 'new'
                            ? 'border-emerald-500 bg-emerald-50 text-emerald-700'
                            : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-50'
                        }`}
                      >
                        New Committee
                      </button>
                      <button
                        type="button"
                        onClick={() => setFieldValue('committeeStatus', 'existing')}
                        className={`rounded-lg border px-4 py-2 text-sm font-medium transition-colors ${
                          values.committeeStatus === 'existing'
                            ? 'border-emerald-500 bg-emerald-50 text-emerald-700'
                            : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-50'
                        }`}
                      >
                        Existing Committee
                      </button>
                    </div>
                    <p className="mt-2 text-xs text-slate-400">Create a brand new committee — enter its planned next meeting date.</p>
                  </div>

                  {/* Name, Type & Authority Level */}
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <label className="mb-1.5 block text-sm font-medium text-slate-700">
                        Name<span className="text-rose-500">*</span>
                      </label>
                      <Field name="name" type="text" placeholder="Board level commit" className={inputClass} />
                      <ErrorMessage name="name" component="div" className={errorClass} />
                    </div>
                    <div>
                      <label className="mb-1.5 block text-sm font-medium text-slate-700">Type</label>
                      <Field as="select" name="type" className={inputClass}>
                        <option>Board</option>
                        <option>Executive</option>
                        <option>Governance</option>
                        <option>Audit</option>
                      </Field>
                      <ErrorMessage name="type" component="div" className={errorClass} />
                    </div>
                    <div>
                      <label className="mb-1.5 block text-sm font-medium text-slate-700">Authority Level</label>
                      <Field as="select" name="authorityLevel" className={inputClass}>
                        <option>board level</option>
                        <option>management level</option>
                        <option>department level</option>
                      </Field>
                      <ErrorMessage name="authorityLevel" component="div" className={errorClass} />
                    </div>
                  </div>

                  {/* Purpose */}
                  <div>
                    <label className="mb-1.5 block text-sm font-medium text-slate-700">Purpose</label>
                    <Field
                      as="textarea"
                      name="purpose"
                      rows={2}
                      placeholder="Enter committee guidelines, intent, or mandates..."
                      className={`${inputClass} resize-none`}
                    />
                    <ErrorMessage name="purpose" component="div" className={errorClass} />
                  </div>

                  {/* Chairman Name, Chairman Email & Secretary */}
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <label className="mb-1.5 block text-sm font-medium text-slate-700">
                        Chairman Name<span className="text-rose-500">*</span>
                      </label>
                      <Field name="chairmanName" type="text" className={inputClass} />
                      <ErrorMessage name="chairmanName" component="div" className={errorClass} />
                    </div>
                    <div>
                      <label className="mb-1.5 block text-sm font-medium text-slate-700">
                        Chairman Email<span className="text-rose-500">*</span>
                      </label>
                      <Field name="chairmanEmail" type="email" className={inputClass} />
                      <ErrorMessage name="chairmanEmail" component="div" className={errorClass} />
                    </div>
                    <div>
                      <label className="mb-1.5 block text-sm font-medium text-slate-700">Secretary</label>
                      <div className="relative">
                        <Field as="select" name="secretary" className={`${inputClass} appearance-none pr-8`}>
                          <option value="Abdullah Al-Jabri">Abdullah Al-Jabri</option>
                          <option value="Other">Other</option>
                        </Field>
                        <ChevronDown size={16} className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-slate-400" />
                      </div>
                      <ErrorMessage name="secretary" component="div" className={errorClass} />
                    </div>
                  </div>

                  {/* Meeting Frequency, Next Meeting Date & Quorum Required */}
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <label className="mb-1.5 block text-sm font-medium text-slate-700">Next Meeting Date</label>
                      <div className="relative">
                        <Field type="date" name="nextMeetingDate" className={`${inputClass} pr-9`} />
                        <Calendar size={16} className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-slate-400" />
                      </div>
                      <ErrorMessage name="nextMeetingDate" component="div" className={errorClass} />
                    </div>
                    <div>
                      <label className="mb-2 block text-sm font-medium text-slate-700">Quorum Required?</label>
                      <div className="flex gap-3">
                        <button
                          type="button"
                          onClick={() => setFieldValue('quorumRequired', false)}
                          className={`flex-1 rounded-lg border px-4 py-2 text-sm font-medium transition-colors ${
                            values.quorumRequired === false
                              ? 'border-emerald-500 bg-emerald-50 text-emerald-700'
                              : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-50'
                          }`}
                        >
                          No
                        </button>
                        <button
                          type="button"
                          onClick={() => setFieldValue('quorumRequired', true)}
                          className={`flex-1 rounded-lg border px-4 py-2 text-sm font-medium transition-colors ${
                            values.quorumRequired === true
                              ? 'border-emerald-500 bg-emerald-50 text-emerald-700'
                              : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-50'
                          }`}
                        >
                          Yes
                        </button>
                      </div>
                    </div>

                    <div>
                      <label className="mb-1.5 block text-sm font-medium text-slate-700">Meeting Frequency</label>
                      <div className="relative">
                        <Field as="select" name="meetingFrequency" className={`${inputClass} appearance-none pr-8`}>
                          <option value="as needed">As Needed</option>
                          <option value="monthly">Monthly</option>
                          <option value="quarterly">Quarterly</option>
                        </Field>
                        <ChevronDown size={16} className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-slate-400" />
                      </div>
                      <ErrorMessage name="meetingFrequency" component="div" className={errorClass} />
                    </div>
                  </div>
                  <p className="-mt-3 text-xs text-slate-400">
                    Frequency uses this date to compute upcoming meetings &amp; secretary reminders.
                  </p>

                  {/* Minimum Attendees Required */}
                  <div>
                    <label className="mb-1.5 block text-sm font-medium text-slate-700">Minimum Attendees Required</label>
                    <Field name="minimumAttendees" type="number" min={1} className={inputClass} />
                    <p className="mt-1.5 text-xs text-slate-400">
                      A meeting is quorate only if at least this many members are marked Present.
                    </p>
                    <ErrorMessage name="minimumAttendees" component="div" className={errorClass} />
                  </div>

                  {/* Members */}
                  <div>
                    <div className="mb-3 flex items-center justify-between">
                      <label className="text-sm font-semibold text-slate-800">Members</label>
                      <button
                        type="button"
                        onClick={() =>
                          setFieldValue('members', [
                            ...values.members,
                            { source: 'manual', name: '', email: '', title: '', role: 'Internal' },
                          ])
                        }
                        className="flex items-center gap-1.5 rounded-lg border border-slate-200 px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50"
                      >
                        <Plus size={14} />
                        Add Member
                      </button>
                    </div>

                    <FieldArray name="members">
                      {({ remove }) => (
                        <div className="space-y-3">
                          {values.members.map((member, index) => (
                            <div key={index} className="rounded-xl border border-slate-200 p-3">
                              <div className="mb-2 flex items-center justify-between">
                                {member.source === 'system' ? (
                                  <>
                                    <span className="text-xs font-medium text-emerald-600">✓ System user</span>
                                    <button
                                      type="button"
                                      onClick={() => setFieldValue(`members.${index}.source`, 'manual')}
                                      className="text-xs font-medium text-emerald-600 hover:underline"
                                    >
                                      Unlink
                                    </button>
                                  </>
                                ) : (
                                  <span className="text-xs font-medium text-slate-400">Manual entry</span>
                                )}
                              </div>

                              <div className="mb-2 grid grid-cols-2 gap-3 mr-[54px]">
                                <div>
                                  <Field name={`members.${index}.name`} placeholder="Name" className={inputClass} />
                                  <ErrorMessage name={`members.${index}.name`} component="div" className={errorClass} />
                                </div>
                                <Field name={`members.${index}.email`} type="email" placeholder="Email" className={inputClass} />
                              </div>

                              <div className="grid grid-cols-[1fr_1fr_auto] gap-3">
                                <Field name={`members.${index}.title`} placeholder="Title / Role" className={inputClass} />
                                <Field as="select" name={`members.${index}.role`} className={inputClass}>
                                  <option value="Internal">Internal</option>
                                  <option value="External">External</option>
                                </Field>
                                <button
                                  type="button"
                                  onClick={() => remove(index)}
                                  className="flex items-center justify-center rounded-lg border border-rose-200 bg-rose-50 px-3 text-rose-500 hover:bg-rose-100"
                                  aria-label="Remove member"
                                >
                                  <Trash2 size={16} />
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </FieldArray>
                  </div>

                  {/* Notes */}
                  <div>
                    <label className="mb-1.5 block text-sm font-medium text-slate-700">Notes</label>
                    <Field as="textarea" name="notes" rows={2} placeholder="Add notes here..." className={`${inputClass} resize-none`} />
                  </div>

                  {/* Create Committee Button */}
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="flex w-full items-center justify-center gap-2 rounded-2xl bg-gradient-to-r from-[#0E7490] to-[#14B8A6] py-3.5 font-medium text-white transition-opacity hover:opacity-90 disabled:opacity-60"
                  >
                    {isSubmitting ? 'Creating...' : 'Create Committee'}
                  </button>
                </Form>
              </div>
            </>
          )}
        </Formik>
      </div>
    </>,
    document.body,
  );
};

export default AddCommitteeDrawer;
